import React, { useEffect, useState } from "react";
import { Plus, KeyRound, Loader2, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { CopyButton, StatusPill } from "@/components/common";

export default function Credentials() {
  const [creds, setCreds] = useState([]);
  const [domains, setDomains] = useState([]);
  const [name, setName] = useState("");
  const [domainId, setDomainId] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null); // newly created cred with password

  const load = () => {
    api.get("/smtp-credentials").then((r) => setCreds(r.data)).catch(() => {});
    api.get("/domains").then((r) => { setDomains(r.data); if (r.data[0] && !domainId) setDomainId(r.data[0].id); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !domainId) return toast.error("Pick a domain and name");
    setBusy(true);
    try {
      const { data } = await api.post("/smtp-credentials", { name: name.trim(), domain_id: domainId });
      setReveal(data);
      setName("");
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this credential?")) return;
    await api.delete(`/smtp-credentials/${id}`);
    toast.success("Credential deleted");
    load();
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">SMTP Credentials</h1>
        <p className="mt-1 text-sm text-slate-400">Plug these into any app, CMS or framework that speaks SMTP.</p>
      </div>

      <form onSubmit={create} className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:grid-cols-[1fr_1fr_auto]">
        <input data-testid="cred-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Credential name (e.g. Production App)"
          className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60" />
        <select data-testid="cred-domain-select" value={domainId} onChange={(e) => setDomainId(e.target.value)}
          className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60">
          {domains.length === 0 && <option value="">Add a domain first</option>}
          {domains.map((d) => <option key={d.id} value={d.id}>{d.domain}</option>)}
        </select>
        <button data-testid="smtp-create-cred-button" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04120c] hover:bg-emerald-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate
        </button>
      </form>

      {reveal && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 glow-emerald" data-testid="cred-reveal">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300"><KeyRound className="h-4 w-4" /> New credential — copy the password now, it won't be shown again</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[["Host", reveal.host], ["Port", "587 (TLS) / 465 (SSL) / 2525"], ["Username", reveal.username], ["Password", reveal.password]].map(([k, v]) => (
              <div key={k}>
                <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-400">{k}</div>
                <div className="flex items-center gap-2">
                  <code className="mono flex-1 truncate rounded-lg border border-slate-800 bg-slate-950 p-2.5 text-xs text-emerald-400">{v}</code>
                  <CopyButton value={String(v)} label={k} testid={`cred-copy-${k.toLowerCase()}`} />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setReveal(null)} className="mt-4 text-xs text-slate-400 hover:text-white">Dismiss</button>
        </div>
      )}

      <div className="space-y-3">
        {creds.length === 0 && <div className="rounded-xl border border-dashed border-slate-800 py-12 text-center text-sm text-slate-500">No credentials yet.</div>}
        {creds.map((c) => (
          <div key={c.id} data-testid={`cred-row-${c.id}`} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-emerald-400"><KeyRound className="h-5 w-5" /></div>
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="mono text-xs text-slate-500">{c.username} · {c.domain} · {c.host}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <StatusPill status={c.status} />
              <button onClick={() => remove(c.id)} data-testid={`cred-delete-${c.id}`} className="text-slate-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
